import { useEffect, useState, FormEvent } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import {
  getForm,
  getSite,
  listFormAttendees,
  appendFormTimelineEntry,
  updateForm,
  type FormRow,
  type FormAttendeeRow,
  type Site,
} from '../lib/db';
import { Modal } from '../components/Modal';
import { PhotoAttach, type LocalPhoto } from '../components/PhotoAttach';
import { supabase } from '../lib/supabase';
import { getSignedUrl } from '../lib/storage';
import { useUserOrg } from '../lib/org';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { RIDDOR_LABELS } from '../lib/riddor';
import {
  generateToolboxTalkPdf,
  generateAccidentPdf,
  generateSubbieInductionPdf,
} from '../lib/pdf';

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

const KIND_LABEL: Record<FormRow['kind'], string> = {
  toolbox_talk: 'Toolbox talk',
  accident: 'Accident / near-miss',
  subcontractor_induction: 'Subbie induction',
  other: 'Form',
};

export function FormView() {
  const { formId } = useParams<{ formId: string }>();
  const { org } = useUserOrg();
  const [form, setForm] = useState<FormRow | null>(null);
  const [site, setSite] = useState<Site | null>(null);
  const [exporting, setExporting] = useState(false);
  const [attendees, setAttendees] = useState<FormAttendeeRow[]>([]);
  const [photoUrls, setPhotoUrls] = useState<string[]>([]);
  const [sigUrl, setSigUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Follow-up modal state (accidents only)
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const [followUpNote, setFollowUpNote] = useState('');
  const [followUpBy, setFollowUpBy] = useState('');
  const [followUpPhotos, setFollowUpPhotos] = useState<LocalPhoto[]>([]);
  const [followUpSubmitting, setFollowUpSubmitting] = useState(false);

  useEffect(() => {
    if (!formId) return;
    (async () => {
      try {
        const f = await getForm(formId);
        setForm(f);
        if (!f) return;
        if (f.site_id) {
          try { setSite(await getSite(f.site_id)); } catch { /* non-fatal */ }
        }
        // Pre-fill follow-up author from auth user
        try {
          const { data: au } = await supabase.auth.getUser();
          const uname = (au.user?.user_metadata?.full_name as string | undefined) ?? '';
          if (uname && !followUpBy) setFollowUpBy(uname);
        } catch { /* noop */ }
        if (f.kind === 'toolbox_talk') {
          setAttendees(await listFormAttendees(f.id));
        }
        const data = f.data as Record<string, unknown>;
        const paths = (data.photo_paths as string[] | undefined) ?? [];
        const bucket = f.kind === 'subcontractor_induction' ? 'subbie-induction-photos' : 'accident-photos';
        if (paths.length > 0) {
          const urls = await Promise.all(paths.map((p) => getSignedUrl(bucket, p, 3600)));
          setPhotoUrls(urls);
        }
        if (f.signature_url) {
          setSigUrl(await getSignedUrl('signatures', f.signature_url, 3600));
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [formId]);

  if (loading) return <AppShell title="Form" back="/"><LoadingIndicator /></AppShell>;
  if (!form) return <AppShell title="Form" back="/"><div className="banner banner-error">{error ?? 'Not found.'}</div></AppShell>;

  const data = form.data as Record<string, unknown>;
  const kindLabel = KIND_LABEL[form.kind];
  const backTo = form.site_id ? `/sites/${form.site_id}/forms` : '/';

  async function exportPdf() {
    if (!org || !form) return;
    setExporting(true);
    try {
      let blob: Blob;
      if (form.kind === 'toolbox_talk') {
        blob = await generateToolboxTalkPdf({
          orgName: org.org_name,
          orgLogoPath: org.logo_path,
          site,
          form,
          attendees,
        });
      } else if (form.kind === 'accident') {
        blob = await generateAccidentPdf({
          orgName: org.org_name,
          orgLogoPath: org.logo_path,
          site,
          form,
        });
      } else if (form.kind === 'subcontractor_induction') {
        blob = await generateSubbieInductionPdf({
          orgName: org.org_name,
          orgLogoPath: org.logo_path,
          site,
          form,
        });
      } else {
        return;
      }
      const url = URL.createObjectURL(blob);
      const dateStr = new Date(form.performed_at).toISOString().slice(0, 10);
      const safeTitle = (form.title ?? form.kind).replace(/\s+/g, '-').replace(/[^a-zA-Z0-9_\-]/g, '');
      const a = document.createElement('a');
      a.href = url;
      a.download = `${safeTitle}-${dateStr}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setExporting(false);
    }
  }

  async function submitFollowUp(e: FormEvent) {
    e.preventDefault();
    if (!form || !org) return;
    if (!followUpNote.trim() || !followUpBy.trim()) {
      setError('Add a note and your name to log the follow-up.');
      return;
    }
    setFollowUpSubmitting(true);
    setError(null);
    try {
      // Upload photos if any
      const paths: string[] = [];
      for (let i = 0; i < followUpPhotos.length; i++) {
        const path = `${org.org_id}/form-${form.id}/timeline-${Date.now()}-${i}.jpg`;
        const { error: upErr } = await supabase.storage
          .from('accident-photos')
          .upload(path, followUpPhotos[i].blob, { contentType: 'image/jpeg', upsert: true });
        if (upErr) throw upErr;
        paths.push(path);
      }
      await appendFormTimelineEntry(form.id, {
        note: followUpNote.trim(),
        by_name: followUpBy.trim(),
        photo_paths: paths,
      });
      // Refresh form
      const refreshed = await getForm(form.id);
      setForm(refreshed);
      setFollowUpNote('');
      setFollowUpPhotos([]);
      setFollowUpOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setFollowUpSubmitting(false);
    }
  }

  return (
    <AppShell title={form.title ?? kindLabel} back={backTo}>
      <div className="card" style={{ marginBottom: 16 }}>
        <p style={{ margin: 0, fontWeight: 600 }}>{kindLabel}</p>
        <p style={{ margin: '4px 0 0', color: 'var(--ink-soft)', fontSize: 14 }}>
          {fmtDateTime(form.performed_at)} · By {form.performed_by_name ?? 'Unknown'}
        </p>
        {form.is_riddor && (
          <p style={{ margin: '8px 0 0' }}>
            <span className="badge badge-danger">RIDDOR-reportable</span>
          </p>
        )}
      </div>

      <button
        type="button"
        className="btn btn-primary"
        onClick={exportPdf}
        disabled={exporting}
        style={{ marginBottom: 16 }}
      >
        {exporting ? 'Exporting…' : 'Export PDF'}
      </button>

      <div className="card">
        {Object.entries(data).filter(([k]) => !['photo_paths', 'timeline', 'riddor_ref', 'riddor_submitted_at', 'reviewed_by', 'reviewed_at', 'review_notes', 'closed_out', 'closed_at', 'cause_root', 'inductor_signature_data_url', 'subcontractor_id'].includes(k)).map(([k, v]) => {
          if (v == null || v === '' || (Array.isArray(v) && v.length === 0)) return null;
          let display: string;
          if (Array.isArray(v) && v.length > 0 && typeof v[0] === 'object') {
            display = (v as Array<Record<string, unknown>>)
              .map((w) => [w.name, w.statement].filter(Boolean).join(' — ')).join('; ');
          } else if (Array.isArray(v)) display = v.map((b) => (b ? '✓' : '×')).join(' ');
          else if (typeof v === 'object') display = JSON.stringify(v);
          else display = String(v);
          return (
            <p key={k} style={{ margin: '0 0 8px' }}>
              <strong style={{ textTransform: 'capitalize', color: 'var(--ink-soft)', fontSize: 13 }}>
                {k.replace(/_/g, ' ')}:
              </strong>{' '}
              {display}
            </p>
          );
        })}
      </div>

      {photoUrls.length > 0 && (
        <>
          <h3 className="section-title" style={{ marginTop: 18 }}>Photos</h3>
          <div className="photo-grid">
            {photoUrls.map((u, i) => (
              <a key={i} href={u} target="_blank" rel="noreferrer" className="photo-thumb static">
                <img src={u} alt={`Attached photo ${i + 1}`} />
              </a>
            ))}
          </div>
        </>
      )}

      {attendees.length > 0 && (
        <>
          <h3 className="section-title" style={{ marginTop: 18 }}>Attendees ({attendees.length})</h3>
          <ul className="person-list">
            {attendees.map((a) => (
              <li key={a.id} className="person-item person-item-done">
                <div>
                  <p className="person-name">
                    {a.full_name}
                    {a.verbal_only && (
                      <span className="status-pill status-muted" style={{ marginLeft: 8 }}>verbal only</span>
                    )}
                  </p>
                  {a.company && <p className="person-meta">{a.company}</p>}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {sigUrl && (
        <>
          <h3 className="section-title" style={{ marginTop: 18 }}>Signature</h3>
          <div className="card" style={{ padding: 8 }}>
            <img src={sigUrl} alt="Signature" style={{ maxWidth: '100%', height: 'auto' }} />
          </div>
        </>
      )}

      {form && form.kind === 'accident' && (
        <AccidentCompliancePanel form={form} onSaved={setForm} />
      )}

      {form && form.kind === 'accident' && (
        <>
          <h3 className="section-title" style={{ marginTop: 24 }}>Investigation timeline</h3>
          <p className="muted" style={{ margin: '0 0 12px', fontSize: 13 }}>
            Log every follow-up action: Toolbox Talk delivered, fix made, root-cause review, etc.
          </p>
          {(() => {
            const tl = ((form.data ?? {}) as { timeline?: Array<{ at: string; by_name: string; note: string; photo_paths?: string[] }> }).timeline ?? [];
            if (tl.length === 0) {
              return (
                <div className="card" style={{ textAlign: 'center', marginBottom: 12 }}>
                  <p style={{ margin: 0, color: 'var(--ink-soft)' }}>No follow-ups logged yet.</p>
                </div>
              );
            }
            return (
              <div className="doc-list">
                {tl.slice().reverse().map((entry, i) => (
                  <div key={i} className="card" style={{ marginBottom: 8 }}>
                    <p style={{ margin: 0 }}><strong>{entry.note}</strong></p>
                    <p className="muted" style={{ margin: '4px 0 0', fontSize: 12 }}>
                      {entry.by_name} · {fmtDateTime(entry.at)}
                      {entry.photo_paths && entry.photo_paths.length > 0 && ` · ${entry.photo_paths.length} photo${entry.photo_paths.length === 1 ? '' : 's'}`}
                    </p>
                  </div>
                ))}
              </div>
            );
          })()}
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setFollowUpOpen(true)}
            style={{ marginTop: 8 }}
          >
            + Add follow-up
          </button>

          <Modal open={followUpOpen} title="Add follow-up" onClose={() => !followUpSubmitting && setFollowUpOpen(false)}>
            <form onSubmit={submitFollowUp}>
              <div className="field needs-input">
                <label htmlFor="fu-note">What happened? *</label>
                <textarea
                  id="fu-note"
                  rows={3}
                  placeholder="e.g. Brick guards installed on perimeter scaffold by ACME Scaffolding."
                  value={followUpNote}
                  onChange={(e) => setFollowUpNote(e.target.value)}
                  required
                />
              </div>
              <div className="field needs-input">
                <label htmlFor="fu-by">Logged by *</label>
                <input
                  id="fu-by"
                  type="text"
                  value={followUpBy}
                  onChange={(e) => setFollowUpBy(e.target.value)}
                  required
                />
              </div>
              <div className="field">
                <label>Photo (optional)</label>
                <PhotoAttach photos={followUpPhotos} onChange={setFollowUpPhotos} />
              </div>
              <div className="btn-stack">
                <button type="submit" className="btn btn-primary" disabled={followUpSubmitting}>
                  {followUpSubmitting ? 'Saving…' : 'Save follow-up'}
                </button>
                <button type="button" className="btn btn-ghost" onClick={() => setFollowUpOpen(false)} disabled={followUpSubmitting}>
                  Cancel
                </button>
              </div>
            </form>
          </Modal>
        </>
      )}

      <div style={{ marginTop: 24 }}>
        <Link to={backTo} className="back-link">‹ Back to forms</Link>
      </div>
    </AppShell>
  );
}


/**
 * Accident follow-up panel (Sep 2026 compliance pass):
 *  - HSE / RIDDOR report: log the F2508 reference + date once reported
 *  - Manager review & close-out: who reviewed it, when, actions complete
 * Both are saved into forms.data so the PDF picks them up.
 */
function AccidentCompliancePanel({ form, onSaved }: { form: FormRow; onSaved: (f: FormRow) => void }) {
  const d = (form.data ?? {}) as Record<string, unknown>;
  const [ref, setRef] = useState(String(d.riddor_ref ?? ''));
  const [refDate, setRefDate] = useState(String(d.riddor_submitted_at ?? ''));
  const [reviewer, setReviewer] = useState(String(d.reviewed_by ?? ''));
  const [reviewDate, setReviewDate] = useState(String(d.reviewed_at ?? ''));
  const [reviewNotes, setReviewNotes] = useState(String(d.review_notes ?? ''));
  const [closed, setClosed] = useState(Boolean(d.closed_out));
  const [saving, setSaving] = useState<null | 'riddor' | 'review'>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const flags = Object.entries((form.riddor_flags ?? {}) as Record<string, boolean>).filter(([, v]) => v);
  const reported = Boolean(d.riddor_ref || d.riddor_submitted_at);
  const incidentDate = String(d.date ?? form.performed_at.slice(0, 10));
  const deadlineDays = flags.some(([k]) => k === 'over7day') ? 15 : 10;
  const deadline = new Date(new Date(incidentDate).getTime() + deadlineDays * 86400000)
    .toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  async function save(kind: 'riddor' | 'review') {
    setSaving(kind); setMsg(null);
    try {
      const patch = kind === 'riddor'
        ? { riddor_ref: ref.trim() || null, riddor_submitted_at: refDate || null }
        : {
            reviewed_by: reviewer.trim(), reviewed_at: reviewDate || null,
            review_notes: reviewNotes.trim(), closed_out: closed,
            closed_at: closed ? (d.closed_at ?? new Date().toISOString().slice(0, 10)) : null,
          };
      const data = { ...d, ...patch };
      await updateForm(form.id, { data });
      onSaved({ ...form, data });
      setMsg('Saved.');
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(null);
    }
  }

  return (
    <>
      {form.is_riddor && (
        <>
          <h3 className="section-title" style={{ marginTop: 24 }}>HSE (RIDDOR) report</h3>
          <div className="card" style={{ marginBottom: 12, background: reported ? '#E8F4EA' : '#FCE7E2', borderColor: reported ? 'var(--success)' : 'var(--danger)' }}>
            <p style={{ margin: '0 0 6px', fontWeight: 600 }}>
              {reported ? '✓ Reported to HSE' : `Not yet reported — deadline ${deadline}`}
            </p>
            <p className="muted" style={{ margin: '0 0 10px', fontSize: 13 }}>
              Reportable because: {flags.map(([k]) => RIDDOR_LABELS[k] ?? k).join('; ') || '—'}.
              {!reported && ' Report at hse.gov.uk/riddor, then log the reference here.'}
            </p>
            <div className="field-row">
              <div className="field"><label htmlFor="fv-ref">HSE reference</label><input id="fv-ref" type="text" value={ref} onChange={(e) => setRef(e.target.value)} /></div>
              <div className="field"><label htmlFor="fv-refdate">Reported on</label><input id="fv-refdate" type="date" value={refDate} onChange={(e) => setRefDate(e.target.value)} /></div>
            </div>
            <button type="button" className="btn btn-primary" disabled={saving !== null} onClick={() => save('riddor')}>
              {saving === 'riddor' ? 'Saving…' : 'Save HSE details'}
            </button>
          </div>
        </>
      )}

      <h3 className="section-title" style={{ marginTop: 24 }}>Manager review &amp; close-out</h3>
      <div className="card" style={{ marginBottom: 12 }}>
        <p className="muted" style={{ margin: '0 0 10px', fontSize: 13 }}>
          A manager or competent person should review every incident and confirm the actions are done.
        </p>
        <div className="field-row">
          <div className="field"><label htmlFor="fv-rev">Reviewed by</label><input id="fv-rev" type="text" value={reviewer} onChange={(e) => setReviewer(e.target.value)} /></div>
          <div className="field"><label htmlFor="fv-revdate">Review date</label><input id="fv-revdate" type="date" value={reviewDate} onChange={(e) => setReviewDate(e.target.value)} /></div>
        </div>
        <div className="field"><label htmlFor="fv-revnotes">Findings / comments</label><textarea id="fv-revnotes" rows={2} value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)} /></div>
        <label className="check-row" style={{ marginBottom: 10 }}>
          <input type="checkbox" checked={closed} onChange={(e) => setClosed(e.target.checked)} />
          <span>All actions complete — close this incident</span>
        </label>
        <button type="button" className="btn btn-primary" disabled={saving !== null} onClick={() => save('review')}>
          {saving === 'review' ? 'Saving…' : 'Save review'}
        </button>
        {msg && <p className="muted" style={{ margin: '8px 0 0', fontSize: 13 }}>{msg}</p>}
      </div>
    </>
  );
}
